import { useEffect, useRef, useState } from "react";
import { ChefHat, Bell, Truck, Snowflake, Volume2, X, History, LogOut, Clock, ArrowLeftRight, Trash2 } from "lucide-react";
import OrderDetailModal from "../components/OrderDetailModal";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import TopBar from "./TopBar";
import { playBell, playDataUrl, unlockAudio } from "../lib/audio";
import { getAuth, getTeam, clearAuth } from "../lib/auth";
import { getDeviceId } from "../lib/device";
import { attachSlotAutoRelease } from "../lib/slotRelease";
import { switchRole } from "../lib/roleSwitch";
import { fmtEUR } from "../lib/format";

const LANES = [
  { id: "maken", label: "Maken", icon: ChefHat, color: "#FF8A00", next: "klaar", nextLabel: "Afgerond" },
];

const VB_TYPES = {
  live: { label: "LIVE", color: "#D62828", icon: ChefHat },
  vooraf: { label: "VOORAF", color: "#118AB2", icon: Snowflake },
  extern: { label: "EXTERN", color: "#333333", icon: Truck },
};

export default function Voorbereider() {
  const navigate = useNavigate();
  const team = getTeam();
  const [orders, setOrders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [lastSeenIds, setLastSeenIds] = useState(new Set());
  const [banner, setBanner] = useState(null); // {order, kind: "klaar"|"afgerond"}
  const [historyOpen, setHistoryOpen] = useState(false);
  const prevKlaarRef = useRef(new Set());
  const prevAfgerondRef = useRef(new Set());

  // Unlock audio on first user interaction
  useEffect(() => {
    const handler = () => {
      unlockAudio();
      window.removeEventListener("click", handler);
      window.removeEventListener("touchstart", handler);
    };
    window.addEventListener("click", handler);
    window.addEventListener("touchstart", handler);
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("touchstart", handler);
    };
  }, []);

  // Auto-release slot bij tab close (alleen voor team-rol, niet admin)
  useEffect(() => {
    const auth = getAuth();
    return attachSlotAutoRelease(auth?.role);
  }, []);

  useEffect(() => {
    api.get("/teams").then(({ data }) => setTeams(data)).catch(() => {});
  }, []);

  const activeTeamData = teams.find((t) => t.id === team?.id) || team;

  const load = async () => {
    if (!team) return;
    try {
      const { data } = await api.get("/orders?limit=200");
      const teamOrders = data.filter(
        (o) =>
          o.betaal_status !== "wachten" &&
          o.status !== "geannuleerd" &&
          o.team_id === team.id
      );      // new in maken -> toast
      const currentMaken = new Set(teamOrders.filter((o) => o.status === "maken").map((o) => o.id));
      if (lastSeenIds.size > 0) {
        for (const id of currentMaken) {
          if (!lastSeenIds.has(id)) {
            const o = teamOrders.find((x) => x.id === id);
            if (o) toast(`Nieuwe order ${o.ordernummer}`, { duration: 3000 });
          }
        }
      }
      setLastSeenIds(currentMaken);

      // klaar geen flash banner meer (voorbereider klikt zelf op afgerond, dat is geen verrassing)
      const currentKlaar = new Set(teamOrders.filter((o) => o.status === "klaar").map((o) => o.id));
      prevKlaarRef.current = currentKlaar;

      // klaar→afgerond: geen flash banner, alleen subtiele toast (op verzoek user)
      const currentAfgerond = new Set(teamOrders.filter((o) => o.status === "afgerond").map((o) => o.id));
      prevAfgerondRef.current = currentAfgerond;

      setOrders(teamOrders);
    } catch (e) {
      // skip
    }
  };

  useEffect(() => {
    if (!team) return;
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.id, activeTeamData?.geluid_data_url]);

  const moveStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      load();
    } catch (e) {
      toast.error("Kon status niet wijzigen");
    }
  };

  const logout = async () => {
    const auth = getAuth();
    const isAdminRole = auth?.role === "admin";
    if (!isAdminRole) {
      try {
        await api.post("/auth/logout", { code: "", device_id: getDeviceId() });
      } catch {
        // best effort
      }
      clearAuth();
      navigate("/");
    } else {
      navigate("/admin");
    }
  };

  if (!team) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <div>
          <div className="font-display text-3xl font-extrabold mb-2">Geen team gekoppeld</div>
          <div className="text-[var(--ink-soft)] mb-4">Log opnieuw in met je team-code.</div>
          <button onClick={() => navigate("/")} className="btn-press px-5 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold">
            Naar login
          </button>
        </div>
      </div>
    );
  }

  const grouped = LANES.map((l) => ({
    ...l,
    orders: orders.filter((o) => o.status === l.id).slice(0, 30),
  }));
  const afgerondList = orders.filter((o) => (o.status === "klaar" || o.status === "afgerond") && !o.voorbereider_archived)
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));

  return (
    <div className="min-h-screen flex flex-col" data-testid="voorbereider-screen">
      <TopBar
        title={`Voorbereider · ${activeTeamData?.naam || team.naam}`}
        subtitle="Alleen orders van dit team"
        color={activeTeamData?.kleur || team.kleur}
      >
        <div
          className="px-3 py-1.5 rounded-full font-bold text-white text-sm"
          style={{ background: activeTeamData?.kleur || team.kleur }}
          data-testid="voorbereider-team-badge"
        >
          {team.letter}
        </div>
        <button
          onClick={async () => {
            try {
              await switchRole("kassa");
              toast.success("Gewisseld naar Kassa");
              navigate("/kassa");
            } catch (e) {
              toast.error(e?.response?.data?.detail || e.message || "Wisselen mislukt");
            }
          }}
          className="btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-[var(--neutral)] text-sm font-bold text-[var(--ink)]"
          data-testid="voorbereider-switch-kassa"
          title="Wissel naar Kassa (zelfde team)"
        >
          <ArrowLeftRight className="w-4 h-4" /> Wissel naar Kassa
        </button>
        <button
          onClick={() => setHistoryOpen(true)}
          className="btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-[var(--neutral)] text-sm font-bold text-[var(--ink)]"
          data-testid="voorbereider-history-btn"
        >
          <History className="w-4 h-4" /> Geschiedenis <span className="text-[var(--ink-soft)]">({afgerondList.length})</span>
        </button>
        <button
          onClick={logout}
          className="btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--ink)] text-white text-sm font-bold"
          data-testid="voorbereider-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Uitloggen
        </button>
      </TopBar>

      <div className="flex-1 max-w-[1200px] w-full mx-auto px-3 md:px-6 py-3 md:py-4 grid grid-cols-1 gap-3">
        {grouped.map((lane) => {
          const Icon = lane.icon;
          return (
            <div
              key={lane.id}
              className="flex flex-col rounded-3xl bg-white border-2 border-[#ecebe6] overflow-hidden"
              data-testid={`lane-${lane.id}`}
              style={{ boxShadow: `inset 0 6px 0 ${lane.color}` }}
            >
              <div className="flex p-4 items-center gap-2 border-b-2 border-[#ecebe6]">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white" style={{ background: lane.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <h2 className="font-display text-2xl font-bold flex-1">{lane.label}</h2>
                <span className="font-display text-2xl font-bold text-[var(--ink-soft)]">{lane.orders.length}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px]">
                {lane.orders.length === 0 ? (
                  <div className="text-center text-[var(--ink-soft)] py-12 text-base" data-testid={`lane-empty-${lane.id}`}>
                    Geen orders in <strong>{lane.label}</strong>
                  </div>
                ) : (
                  lane.orders.map((o) => (
                    <OrderCard key={o.id} order={o} nextStatus={lane.next} nextLabel={lane.nextLabel} onAdvance={moveStatus} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {banner && (
        <FlashBanner order={banner.order} kind={banner.kind} onClose={() => setBanner(null)} />
      )}

      {historyOpen && (
        <HistorySheet
          orders={afgerondList}
          teamId={team.id}
          teamKleur={activeTeamData?.kleur || team.kleur}
          onClose={() => setHistoryOpen(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function FlashBanner({ order, kind, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [onClose]);
  const labels = {
    klaar: { tag: "Order klaar", color: order.team_kleur, icon: Volume2 },
    afgerond: { tag: "Afgerond ✓", color: "#0E8F2A", icon: Bell },
  };
  const cfg = labels[kind] || labels.klaar;
  const Icon = cfg.icon;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.6)" }}
      data-testid={`flash-banner-${kind}`}
      role="dialog"
      aria-label={cfg.tag}
    >
      <div
        className="rounded-3xl px-10 py-8 text-center shadow-2xl max-w-2xl w-full"
        style={{ background: cfg.color }}
      >
        <div className="inline-flex items-center gap-2 text-white/90 text-sm uppercase tracking-[0.3em] font-bold mb-2">
          <Icon className="w-5 h-5" /> {cfg.tag}
        </div>
        <div
          className="text-white font-display font-extrabold leading-none"
          style={{ fontSize: "clamp(7rem, 22vw, 16rem)" }}
          data-testid={`flash-banner-number`}
        >
          {order.ordernummer}
        </div>
        <div className="text-white/90 font-display text-2xl mt-2">{order.team_naam}</div>
        <button
          onClick={onClose}
          className="btn-press mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white/20 hover:bg-white/30 text-white font-bold backdrop-blur"
          data-testid="flash-banner-close"
        >
          <X className="w-4 h-4" /> Sluiten
        </button>
      </div>
    </div>
  );
}

function HistorySheet({ orders, teamId, teamKleur, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [selected, setSelected] = useState(null);

  const removeOne = async (orderId, ordernummer) => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/archive-voorbereider`);
      toast.success(`Order ${ordernummer} uit geschiedenis gehaald`);
      onChanged?.();
    } catch {
      toast.error("Kon order niet verwijderen");
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/archive-voorbereider/all", { team_id: teamId });
      toast.success(`${data.count || 0} order(s) gewist uit geschiedenis`);
      setConfirmClear(false);
      onChanged?.();
    } catch {
      toast.error("Kon geschiedenis niet wissen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch md:items-center justify-end md:justify-center p-0 md:p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="history-sheet"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full md:max-w-2xl md:rounded-3xl flex flex-col max-h-[90vh] md:max-h-[80vh] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b-2 border-[#ecebe6]" style={{ borderTopColor: teamKleur }}>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
              Voorbereider · Geschiedenis
            </div>
            <div className="font-display text-2xl font-extrabold inline-flex items-center gap-2">
              <History className="w-5 h-5" /> Afgerond ({orders.length})
            </div>
          </div>
          <div className="flex items-center gap-2">
            {orders.length > 0 && (
              <button
                onClick={() => setConfirmClear(true)}
                disabled={busy}
                className="btn-press inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--danger)] text-white text-xs font-bold disabled:opacity-50"
                data-testid="history-clear-all"
              >
                <Trash2 className="w-4 h-4" /> Wis alles
              </button>
            )}
            <button
              onClick={onClose}
              className="btn-press w-11 h-11 rounded-full bg-[var(--neutral)] flex items-center justify-center"
              data-testid="history-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {orders.length === 0 ? (
            <div className="text-center text-[var(--ink-soft)] py-12" data-testid="history-empty">
              Nog geen afgeronde orders vandaag.
            </div>
          ) : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div
                  key={o.id}
                  onClick={() => setSelected(o)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(o); } }}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-[var(--neutral)] hover:bg-[#e8e6df] transition-colors cursor-pointer btn-press"
                  data-testid={`history-row-${o.ordernummer}`}
                  aria-label={`Bekijk order ${o.ordernummer}`}
                >
                  <div
                    className="font-display font-extrabold text-2xl px-3 py-1 rounded-xl text-white leading-none"
                    style={{ background: o.team_kleur }}
                  >
                    {o.ordernummer}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">
                      {o.items.map((i) => `${i.aantal}× ${i.naam}`).join(", ")}
                    </div>
                    <div className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {timeStr(o.updated_at || o.created_at)} · {fmtEUR(o.totaal)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeOne(o.id, o.ordernummer); }}
                    disabled={busy}
                    className="btn-press w-9 h-9 rounded-full bg-white border-2 border-[var(--danger)] text-[var(--danger)] flex items-center justify-center disabled:opacity-40"
                    title="Verwijder uit geschiedenis"
                    data-testid={`history-remove-${o.ordernummer}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} />}

        {confirmClear && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            data-testid="history-clear-confirm"
            onClick={(e) => e.target === e.currentTarget && setConfirmClear(false)}
          >
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl border-4 border-[var(--danger)]">
              <div className="font-display text-xl font-extrabold text-[var(--danger)] mb-2">
                Hele geschiedenis wissen?
              </div>
              <p className="text-sm text-[var(--ink-soft)] mb-4">
                Alle <strong>{orders.length}</strong> orders verdwijnen uit deze geschiedenis-lijst.
                Ze blijven wel zichtbaar in admin-rapporten en de Kassa-historie.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmClear(false)}
                  className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
                  data-testid="history-clear-cancel"
                >
                  Annuleer
                </button>
                <button
                  onClick={clearAll}
                  disabled={busy}
                  className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-50"
                  data-testid="history-clear-confirm-btn"
                >
                  {busy ? "Bezig…" : "Wis alles"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, nextStatus, nextLabel, onAdvance }) {
  const age = ageMinutes(order.created_at);
  const isNew = order.status === "maken" && age < 1;
  return (
    <div
      className={`rounded-2xl bg-white border-2 overflow-hidden ${isNew ? "animate-slide-in" : ""}`}
      style={{ borderColor: order.team_kleur }}
      data-testid={`order-card-${order.ordernummer}`}
    >
      <div className="px-4 pt-4 pb-2 flex items-center gap-3" style={{ background: order.team_kleur + "12" }}>
        <div
          className="font-display font-extrabold text-5xl md:text-6xl px-4 py-1 rounded-2xl text-white leading-none"
          style={{ background: order.team_kleur }}
          data-testid={`order-number-${order.ordernummer}`}
        >
          {order.ordernummer}
        </div>
        <div className="flex-1 min-w-0 text-right">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] font-semibold">
            {order.team_naam}
          </div>
          <div className="text-xs text-[var(--ink-soft)] font-medium">{age} min</div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {order.items.map((i, idx) => {
          const vb = VB_TYPES[i.voorbereidingstype];
          return (
            <div key={idx} className="flex items-start gap-2">
              <span className="font-display font-extrabold text-2xl text-[var(--ink)] leading-none w-10">{i.aantal}×</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="font-display font-bold text-xl truncate">{i.naam}</span>
                  {vb && (
                    <span
                      className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full text-white font-bold"
                      style={{ background: vb.color }}
                      data-testid={`vb-badge-${i.voorbereidingstype}`}
                    >
                      {vb.label}
                    </span>
                  )}
                </div>
                {i.productnotitie && (
                  <div className="text-xs text-[var(--ink-soft)] italic">📝 {i.productnotitie}</div>
                )}
              </div>
            </div>
          );
        })}
        <div className="text-xs uppercase tracking-widest font-bold text-[var(--ink-soft)] pt-2 border-t border-[var(--neutral)] mt-2">
          Betaald via {order.betaalmethode}
        </div>
        {order.notitie && (
          <div className="mt-2 p-2 rounded-xl bg-[#FFF4D6] text-sm font-semibold" data-testid={`note-${order.ordernummer}`}>
            📝 {order.notitie}
          </div>
        )}
      </div>
      {nextStatus && (
        <button
          onClick={() => onAdvance(order.id, nextStatus)}
          className="btn-press w-full font-display font-extrabold text-2xl text-white"
          style={{ background: order.team_kleur, minHeight: "72px" }}
          data-testid={`advance-${order.ordernummer}-to-${nextStatus}`}
        >
          {nextLabel} →
        </button>
      )}
    </div>
  );
}

function ageMinutes(iso) {
  try {
    const t = new Date(iso).getTime();
    return Math.max(0, Math.floor((Date.now() - t) / 60000));
  } catch {
    return 0;
  }
}

function timeStr(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
